import _ from "lodash";
import React, { useMemo } from "react";

// components
import MediaWrapper from "../MediaWrapper";
import MediaCardWrapper from "AppComponents/Attachment/MediaCardWrapper/MediaCardWrapper";
import CompactAudioWaveformPlayer from "UIComponents/CompactAudioWaveformPlayer";

// design system
import { MediaCard } from "@toddle-edu/ds-web";

// third party
import classNames from "classnames";

// utils
import { getThumbUrl } from "Utils";

// scss
import classes from "./MediaContainer.scss";

const MEDIA_VISIBILITY = {
  HIDDEN: "HIDDEN",
  ONLY_HOVER: "ONLY_HOVER",
  PERSISTENT: "PERSISTENT",
};

const DEFAULT_MEDIA_DIMENSIONS = {
  width: 300,
  height: 300,
};

const VISUAL_GRID_SIZE = 4;

const ATTACHMENT_TYPES = {
  IMAGE: "IMAGE",
  VIDEO: "VIDEO",
  FILE: "FILE",
  AUDIO: "AUDIO",
  LINK: "LINK",
};

// 1 image or video attachment viewer
const SingleVisualMediaContainer = props => {
  const { attachment, onMediaClick, mediaWrapperContainerStyle } = props;

  const { type, metadata } = attachment;

  const isVideo = type === ATTACHMENT_TYPES.VIDEO;

  const srcWidth = metadata?.width || DEFAULT_MEDIA_DIMENSIONS.width;
  const srcHeight = metadata?.height || DEFAULT_MEDIA_DIMENSIONS.height;

  const ratio = Math.min(400 / srcWidth, 300 / srcHeight);

  const width = Math.floor(srcWidth * ratio);
  const height = Math.floor(srcHeight * ratio);

  return (
    <MediaWrapper
      onMediaClick={onMediaClick}
      iconVisibility={isVideo ? MEDIA_VISIBILITY.PERSISTENT : undefined}
      attachment={attachment}
      width={width}
      height={height}
      containerStyle={mediaWrapperContainerStyle}
    />
  );
};

// 2 or more than 2 image or video attachments viewer
const MultiVisualMediaContainer = props => {
  const { attachments, onMediaClick, mediaWrapperContainerStyle } = props;

  const containerClass = classNames({
    [classes.twoVisualMediaContainer]: true,
    [classes.multiVisualMediaContainer]: _.size(attachments) > 2,
  });

  return (
    <div className={containerClass}>
      {_.map(attachments, attachment => {
        const { id, type } = attachment;
        const isVideo = type === ATTACHMENT_TYPES.VIDEO;

        return (
          <MediaWrapper
            key={id}
            onMediaClick={onMediaClick}
            iconVisibility={isVideo ? MEDIA_VISIBILITY.PERSISTENT : undefined}
            attachment={attachment}
            height={170}
            width={170}
            imgClassName={classes.image}
            thumbnailResolution={DEFAULT_MEDIA_DIMENSIONS}
            containerStyle={mediaWrapperContainerStyle}
          />
        );
      })}
    </div>
  );
};

// 3 or more than 3 image or video attachments viewer in a grid format
const GridVisualMediaContainer = props => {
  const { attachments, onMediaClick, mediaWrapperContainerStyle } = props;

  const gridItems = useMemo(() => {
    // we only want to show the first 4 attachments in grid view
    // remaining attachments would be visible with TheatreMode
    const firstFour = _.filter(
      attachments,
      (_attachment, idx) => idx < VISUAL_GRID_SIZE
    );

    // this will give us an array of 2 objects having two attachments each.
    return _.reduce(
      firstFour,
      (result, attachment, idx) => {
        const arrIdxToPushTo = idx < 2 ? 0 : 1;
        result[arrIdxToPushTo].attachments.push(attachment);
        return result;
      },
      [
        { id: 1, attachments: [] },
        { id: 2, attachments: [] },
      ]
    );
  }, [attachments]);

  return (
    <div className={classes.gridVisualMediaContainer}>
      {_.map(gridItems, (grid, rowIdx) => (
        <div className={classes.row} key={grid.id}>
          {_.map(grid.attachments, (attachment, colIdx) => {
            const { id, type } = attachment;
            const isVideo = type === ATTACHMENT_TYPES.VIDEO;

            const isLastGridItem = rowIdx === 1 && colIdx === 1;
            const remainingAttachmentsCount =
              _.size(attachments) - VISUAL_GRID_SIZE;
            const hasRemainingAttachmentsOnLastGrid =
              isLastGridItem && remainingAttachmentsCount > 0;

            return (
              <MediaWrapper
                width={170}
                height={170}
                onMediaClick={onMediaClick}
                key={id}
                attachment={attachment}
                imgClassName={classes.image}
                thumbnailResolution={DEFAULT_MEDIA_DIMENSIONS}
                overlayVisibility={
                  hasRemainingAttachmentsOnLastGrid
                    ? MEDIA_VISIBILITY.PERSISTENT
                    : undefined
                }
                iconVisibility={
                  isVideo || hasRemainingAttachmentsOnLastGrid
                    ? MEDIA_VISIBILITY.PERSISTENT
                    : undefined
                }
                customIcon={
                  hasRemainingAttachmentsOnLastGrid ? (
                    <div
                      className={classNames(
                        "text-textOn",
                        "text-label-1",
                        classes.count
                      )}
                    >
                      {`+ ${remainingAttachmentsCount}`}
                    </div>
                  ) : undefined
                }
                containerStyle={mediaWrapperContainerStyle}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
};

// Single link attachment viewer
const SingleLinkAttachment = props => {
  const { attachment, onMediaClick } = props;
  const { type, name, url } = attachment;

  const { thumbUrl } = getThumbUrl({
    attachment: attachment,
    width: 300,
    height: 300,
    fitIn: true,
  });

  const handleClick = () => {
    onMediaClick(attachment);
  };

  return (
    <MediaCard
      mimeType={null}
      thumbUrl={thumbUrl}
      name={name}
      type={type}
      showSubtext
      onClick={handleClick}
      cardType="large"
      url={url}
    />
  );
};

// Multiple types of attachment types viewer
const MultiTypeAttachments = props => {
  const { attachments, onMediaClick } = props;

  const compactPlayerContainerClass = classNames({
    [classes.compactPlayerContainer]: true,
  });

  const containerClass = classNames(classes.multiTypeAttachmentsContainer);

  return (
    <div className={containerClass}>
      {_.map(attachments, attachment => {
        const { id, type, url } = attachment;

        if (_.size(attachments) === 1 && type === ATTACHMENT_TYPES.LINK) {
          return (
            <SingleLinkAttachment
              key={id}
              attachment={attachment}
              onMediaClick={onMediaClick}
            />
          );
        }

        if (type === ATTACHMENT_TYPES.AUDIO) {
          return (
            <div key={url}>
              <div className={compactPlayerContainerClass}>
                <CompactAudioWaveformPlayer attachment={attachment} />
              </div>
            </div>
          );
        }

        return (
          <div key={id}>
            <MediaCardWrapper
              className={classes.mediaCardWrapper}
              attachment={attachment}
              mediaCardSize={"medium"}
              mode={"view"}
            />
          </div>
        );
      })}
    </div>
  );
};

export {
  SingleVisualMediaContainer,
  MultiVisualMediaContainer,
  GridVisualMediaContainer,
  SingleLinkAttachment,
  MultiTypeAttachments,
};
